import type { AxiosResponse } from "axios";
import { format } from "date-fns";
import { baseUrlApiWithoutTenant } from "../../commons/RootURL";
import {
  ReservationResponse,
  ReservationVersionResponse,
  DtoReservation,
  Reservation,
  ReservationVersionResponseDto
} from "../../types/reservation";
import { HttpClientNormal } from "../entities/HttpClient";
import { HttpResponse } from "../entities/AxiosMethod";
import { EventDetails } from "./Model";

export class ReservationService {
  private _httpClient: HttpClientNormal = new HttpClientNormal();
  private _urlVersion: string = `${baseUrlApiWithoutTenant}group-calendars/versions`;
  private _urlReservationsList: string = `${baseUrlApiWithoutTenant}group-calendars/reservations/list`;
  private _urlDeltaReservations: string = `${baseUrlApiWithoutTenant}group-calendars/reservations/deltas`;

  async getReservationsByUserIds(
    groupCalendarUserIds: string[],
    startTime: Date,
    endTime: Date,
    isGetAll: boolean = false
  ): Promise<HttpResponse<ReservationResponse>> {
    try {
      const formatDateTimeWithoutTimezone = (date: Date): string => {
        return format(date, "yyyy-MM-dd'T'HH:mm:ss");
      };

      const requestBody = {
        groupCalendarUserIds,
        isGetAll,
        startTime: formatDateTimeWithoutTimezone(startTime),
        endTime: formatDateTimeWithoutTimezone(endTime)
      };

      const response: AxiosResponse = await this._httpClient.post(
        this._urlReservationsList,
        requestBody
      );

      // Transform the response data into proper class instances
      const transformedData: ReservationResponse = Array.isArray(response?.data)
        ? response.data.map(
            (dto: any) =>
              new DtoReservation({
                calendarId: dto.calendarId,
                version: dto.version,
                reservations: (dto.reservations || []).map((r: any) => new Reservation(r)),
                readAccessLevel: dto.permissionRole
              })
          )
        : [];

      return new HttpResponse<ReservationResponse>(
        { ...response, data: transformedData } as AxiosResponse,
        undefined
      );
    } catch (error) {
      console.error("Error fetching reservations by user IDs:", error);
      return new HttpResponse<ReservationResponse>(undefined);
    }
  }
  async getCalendarEventsVersion(): Promise<HttpResponse<ReservationVersionResponse>> {
    try {
      const url = `${this._urlVersion}`;
      const response: AxiosResponse = await this._httpClient.get(url);
      const transformedData = Array.isArray(response?.data)
        ? response.data.map(
            (dto: any) =>
              ({
                userId: dto.userId,
                version: dto.version,
                readAccessLevel: dto.permissionRole
              }) as ReservationVersionResponseDto
          )
        : [];

      return new HttpResponse<ReservationVersionResponse>(
        { ...response, data: transformedData } as AxiosResponse,
        undefined
      );
    } catch (error) {
      console.error("Error fetching calendar events version:", error);
      return new HttpResponse<ReservationVersionResponse>(undefined);
    }
  }
  async getDeltaReservations(
    calendars: {
      groupCalendarUserId: string;
      version: string | null;
    }[]
  ): Promise<HttpResponse<ReservationResponse>> {
    try {
      const requestBody = {
        calendars
      };
      const response: AxiosResponse = await this._httpClient.post(
        this._urlDeltaReservations,
        requestBody
      );
      const transformedData: ReservationResponse = Array.isArray(response?.data)
        ? response.data.map(
            (dto: any) =>
              new DtoReservation({
                calendarId: dto.calendarId,
                version: dto.version,
                reservations: (dto.reservations || []).map((r: any) => new Reservation(r))
              })
          )
        : [];
      return new HttpResponse<ReservationResponse>(
        { ...response, data: transformedData } as AxiosResponse,
        undefined
      );
    } catch (error) {
      console.error("Error fetching delta reservations:", error);
      return new HttpResponse<ReservationResponse>(undefined);
    }
  }

  async getEventDetails(event: EventDetails): Promise<any> {
    try {
      const url = `${baseUrlApiWithoutTenant}group-calendars/reservations/get-details`;
      const response: AxiosResponse = await this._httpClient.post(url, event);
      return response.data as any[];
    } catch (error) {
      console.error("Error fetching event details:", error);
      return [];
    }
  }
}
